import { persistQueryClient } from "@tanstack/query-persist-client-core";
import type { Query, QueryClient } from "@tanstack/react-query";
import { getBuildNumber } from "../utils/environment";
import {
  createQueryClient,
  isInitialLoadQueryKey,
  ORGANISATION_DATA_QUERY_ROOT,
  TWENTY_FOUR_HOURS
} from "./queryConfig";
import { queryPersister } from "./queryPersister";

// App-wide singleton TanStack Query client used by the root QueryClientProvider.
export const queryClient = createQueryClient();

// Only successful initial-load queries are written to the persisted snapshot;
// everything else (booking/customer/payment data, in-flight or errored queries)
// stays in memory, keeping the snapshot small and free of personal data.
export const shouldPersistQuery = (query: Query): boolean =>
  query.state.status === "success" && isInitialLoadQueryKey(query.queryKey);

// Persist and rehydrate the initial-load queries so a returning user paints
// from cached data without an initial network wait. Restoration begins at
// module load (while RootApp paints the auth-check skeleton) and completes
// before the gated initial-load queries activate. Only the initial-load keys
// are persisted — booking/customer/payment data stays in memory to keep the
// snapshot small and free of personal data. The buster is the build number, so
// a new build discards a snapshot written against an older schema; maxAge
// bounds how stale a restored snapshot may be.
export const [, queryClientPersistReady] = persistQueryClient({
  queryClient,
  persister: queryPersister,
  maxAge: TWENTY_FOUR_HOURS,
  buster: getBuildNumber(),
  dehydrateOptions: {
    shouldDehydrateQuery: shouldPersistQuery
  }
});

// The restored organisation snapshot is scoped to the Collins user and
// workspace organisation, so another organisation's data cannot be read.
// Invalidate every scope on restore so the active query is revalidated on mount.
export const invalidateRestoredOrganisationData = (
  client: QueryClient
): Promise<void> =>
  client.invalidateQueries({
    queryKey: [ORGANISATION_DATA_QUERY_ROOT],
    refetchType: "none"
  });

queryClientPersistReady
  .then(() => invalidateRestoredOrganisationData(queryClient))
  .catch(() => undefined);
